import { configureStore } from "@reduxjs/toolkit";

// Guest Slices
import { activeCompanySlice, cartSlice, categoriesBarSlice, guestBlurSlice, packagesSlice, productDetailsSlice, productsSlice, relatedProductsSlice, termSlice,setTotalPrice,multipleCategoryProductsSlice, OrderCompletedImagesSlices, dateTimeSlice, guestLocationIdSlice } from "./slices/guestSlices";


const defaultCartItems = {
  Ralphs: [],
};

const getCartItemsFromStorage = () => {
  try {
    const storedCartItems = JSON.parse(localStorage.getItem("cartItems"));
    if (
      storedCartItems &&
      typeof storedCartItems === "object" &&
      !Array.isArray(storedCartItems)
    ) {
      return { ...defaultCartItems, ...storedCartItems };
    }
  } catch {
    // Ignore malformed legacy storage and start with a valid cart shape.
  }

  return { ...defaultCartItems };
};

const cartItemsFromStorage = getCartItemsFromStorage();

// Restore product caches from localStorage synchronously for initial state
// This ensures all cached categories are available immediately
let multipleCategoryProductsFromStorage = null;
let productDetailsCacheFromStorage = {};

if (typeof window !== 'undefined') {
  try {
    const stored = localStorage.getItem("multipleCategoryProducts");
    if (stored) {
      multipleCategoryProductsFromStorage = JSON.parse(stored);
    }
  } catch (err) {
    console.warn("Failed to parse multipleCategoryProducts from localStorage:", err);
  }
  
  try {
    const stored = localStorage.getItem("productDetailsCache");
    if (stored) {
      productDetailsCacheFromStorage = JSON.parse(stored);
    }
  } catch (err) {
    console.warn("Failed to parse productDetailsCache from localStorage:", err);
  }
}

// Get the default structure from the slice
const getDefaultMultipleCategoryProducts = () => {
  // Import the slice to get its default structure
  const PRODUCT_CATEGORIES = require('../constants/ProductCategories').default;
  return PRODUCT_CATEGORIES.reduce((companiesAcc, company) => {
    const companyCategories = company.categories;
    const productCategoryMap = companyCategories.reduce((acc, productCategory) => {
      const productProperties = {
        loading: false,
        loadingFromCache: false,
        products: [],
        error: '',
        total: 0,
        load: 0,
        endPage: 0,
        actualPage: 1,
        firstLoadInMobileDone: false
      };
      return {
        ...acc,
        [productCategory]: productProperties
      };
    }, {});
    return {
      ...companiesAcc,
      [company.companyName]: {
        ...productCategoryMap,
        firstProductsLoaded: false,
        error: '',
        containersMap: {},
        containerShown: []
      }
    };
  }, {});
};

// Optimized: Only merge cached categories, skip empty ones
// Start with default structure, then overlay cached data
let mergedMultipleCategoryProducts = null;
const defaultStructure = getDefaultMultipleCategoryProducts();
mergedMultipleCategoryProducts = { ...defaultStructure };

// Overlay cached data on top of default structure
if (multipleCategoryProductsFromStorage) {
  // Process all companies from default structure
  Object.keys(defaultStructure).forEach(companyName => {
    const defaultCompany = defaultStructure[companyName];
    const cachedCompany = multipleCategoryProductsFromStorage[companyName];
    
    // If company has cached data, merge it
    if (cachedCompany) {
      // Only merge categories that have cached products (skip empty ones)
      Object.keys(cachedCompany).forEach(category => {
        // Skip metadata fields
        if (category !== 'containersMap' && category !== 'containerShown' && category !== 'firstProductsLoaded' && category !== 'error') {
          const cachedCategory = cachedCompany[category];
          // Only merge if category has products
          if (cachedCategory && cachedCategory.products && cachedCategory.products.length > 0) {
            // Merge only cached categories - use default structure as base
            mergedMultipleCategoryProducts[companyName][category] = {
              ...(defaultCompany[category] || {}),
              products: cachedCategory.products,
              total:
                cachedCategory.total ||
                defaultCompany[category]?.total ||
                cachedCategory.products.length,
              endPage:
                cachedCategory.endPage || defaultCompany[category]?.endPage || 1,
              cachedAt: cachedCategory.cachedAt || 0,
            };
          }
        } else if (cachedCompany[category] !== undefined) {
          // Preserve metadata fields from cache if they exist
          mergedMultipleCategoryProducts[companyName][category] = cachedCompany[category];
        }
      });
    }
  });

  // Also hydrate companies present in cache but missing from defaults
  Object.keys(multipleCategoryProductsFromStorage).forEach((companyName) => {
    if (mergedMultipleCategoryProducts[companyName]) return;
    const cachedCompany = multipleCategoryProductsFromStorage[companyName];
    if (!cachedCompany || typeof cachedCompany !== "object") return;
    mergedMultipleCategoryProducts[companyName] = {
      containersMap: {},
      containerShown: {},
      firstProductsLoaded: false,
      error: "",
    };
    Object.keys(cachedCompany).forEach((category) => {
      if (
        category === "containersMap" ||
        category === "containerShown" ||
        category === "firstProductsLoaded" ||
        category === "error"
      ) {
        if (cachedCompany[category] !== undefined) {
          mergedMultipleCategoryProducts[companyName][category] =
            cachedCompany[category];
        }
        return;
      }
      const cachedCategory = cachedCompany[category];
      if (cachedCategory?.products?.length > 0) {
        mergedMultipleCategoryProducts[companyName][category] = {
          products: cachedCategory.products,
          total: cachedCategory.total || cachedCategory.products.length,
          endPage: cachedCategory.endPage || 1,
          loading: false,
          error: "",
          load: 0,
          cachedAt: cachedCategory.cachedAt || 0,
        };
      }
    });
  });
}

const initialState = {
  cart: { cartItems: cartItemsFromStorage },
  // Hydrate from localStorage cache on initial load, merged with default structure
  ...(mergedMultipleCategoryProducts && {
    multipleCategoryProducts: mergedMultipleCategoryProducts
  }),
  productDetails: {
    loading: false,
    details: {},
    error: "",
    cache: productDetailsCacheFromStorage,
  },
};

// Middleware to persist product caches to localStorage
// Only persists on relevant actions to avoid performance issues
// Debounce writes to batch multiple category updates
let writeTimeout = null;

const persistProductCache = (store) => (next) => (action) => {
  const result = next(action);
  
  // Only persist on actions that modify product data
  const relevantActions = [
    'productDetails/getProductDetails/fulfilled',
    'productDetails/getMultipleCategoryProducts/fulfilled',
    'productDetails/clearProductCache',
  ];
  
  if (!relevantActions.includes(action.type)) {
    return result;
  }
  
  const state = store.getState();
  
  // Debounce localStorage writes to batch multiple category updates
  // This reduces write overhead during initial page load
  const persistToStorage = () => {
    try {
      // Helper: Sanitize product for category list (NewProductCard needs)
      const sanitizeCategoryProduct = (product) => {
        if (!product) return null;
        return {
          productId: product.productId || product._id,
          _id: product._id || product.productId,
          upc: product.upc,
          title: product.title || product.description || product.name,
          description: product.description || product.name || product.title,
          imageLink: product.imageLink || product.images?.[0]?.sizes?.[0]?.url || product.images?.[0]?.sizes?.[1]?.url,
          images: product.images ? product.images.map(img => ({
            perspective: img.perspective,
            sizes: img.sizes?.map(s => ({ size: s.size, url: s.url }))
          })) : [],
          category: product.category || product.categories,
          categories: product.categories || (product.category ? [product.category] : []),
          productWeight: product.productWeight || product.items?.[0]?.size,
          availability: product.availability || (product.items?.[0]?.inventory?.stockLevel ? 'In Stock' : 'Out of Stock'),
          _categoryName: product._categoryName,
          _categoryIndex: product._categoryIndex,
          // Essential items[0] data for pricing
          items: product.items?.[0] ? [{
            price: {
              regular: product.items[0].price?.regular,
              promo: product.items[0].price?.promo,
            },
            size: product.items[0].size,
            soldBy: product.items[0].soldBy,
            upc: product.items[0].upc,
            itemId: product.items[0].itemId,
          }] : [],
        };
      };

      // Helper: Sanitize product details (ProductDetailsCard needs)
      const sanitizeProductDetails = (product) => {
        if (!product) return null;
        return {
          productId: product.productId || product._id,
          _id: product._id || product.productId,
          upc: product.upc,
          description: product.description || product.name || product.title,
          name: product.name || product.description || product.title,
          images: product.images ? product.images.map(img => ({
            perspective: img.perspective,
            sizes: img.sizes?.map(s => ({ size: s.size, url: s.url }))
          })) : [],
          brand: product.brand,
          categories: product.categories || (product.category ? [product.category] : []),
          // Essential items[0] data
          items: product.items?.[0] ? [{
            price: {
              regular: product.items[0].price?.regular,
              promo: product.items[0].price?.promo,
            },
            size: product.items[0].size,
            soldBy: product.items[0].soldBy,
            upc: product.items[0].upc,
            itemId: product.items[0].itemId,
            fulfillment: product.items[0].fulfillment,
            inventory: product.items[0].inventory,
          }] : [],
          // Additional details for product page
          temperature: product.temperature,
          manufacturerDeclarations: product.manufacturerDeclarations,
          allergensDescription: product.allergensDescription,
          ratingsAndReviews: product.ratingsAndReviews ? {
            averageOverallRating: product.ratingsAndReviews.averageOverallRating,
            totalReviewCount: product.ratingsAndReviews.totalReviewCount,
          } : null,
        };
      };

      // Persist multipleCategoryProducts - but limit size
      if (state.multipleCategoryProducts) {
        // Keep lazy-loaded shelves: persist recently used categories (not just
        // the densest ones), and cap products per category for storage size.
        const sanitized = {};
        const MAX_CATEGORIES_PER_COMPANY = 48;
        const MAX_PRODUCTS_PER_CATEGORY = 24;
        
        Object.keys(state.multipleCategoryProducts).forEach(companyName => {
          const company = state.multipleCategoryProducts[companyName];
          sanitized[companyName] = {};
          
          // Collect all categories with products
          const categoriesWithProducts = [];
          Object.keys(company).forEach(category => {
            if (category !== 'containersMap' && category !== 'containerShown' && category !== 'firstProductsLoaded' && category !== 'error') {
              const catData = company[category];
              if (catData && catData.products && catData.products.length > 0) {
                categoriesWithProducts.push({
                  category,
                  cachedAt: catData.cachedAt || 0,
                  productCount: catData.products.length,
                  data: catData
                });
              }
            }
          });
          
          // Prefer most recently loaded/viewed shelves so lazy categories survive reload
          const topCategories = categoriesWithProducts
            .sort((a, b) => {
              if (b.cachedAt !== a.cachedAt) return b.cachedAt - a.cachedAt;
              return b.productCount - a.productCount;
            })
            .slice(0, MAX_CATEGORIES_PER_COMPANY);
          
          topCategories.forEach(({ category, data }) => {
            sanitized[companyName][category] = {
              products: data.products
                .slice(0, MAX_PRODUCTS_PER_CATEGORY)
                .map(sanitizeCategoryProduct)
                .filter(Boolean),
              total: data.total,
              endPage: data.endPage,
              cachedAt: data.cachedAt || Date.now(),
            };
          });
        });
        
        const serialized = JSON.stringify(sanitized);
        // Limit cache size to ~4MB (localStorage limit is usually 5-10MB)
        if (serialized.length < 4 * 1024 * 1024) {
          localStorage.setItem("multipleCategoryProducts", serialized);
        } else {
          console.warn("multipleCategoryProducts cache too large, skipping persistence");
        }
      }
      
      // Persist productDetails cache - limit to last 50 products
      if (state.productDetails?.cache) {
        const cache = state.productDetails.cache;
        const cacheKeys = Object.keys(cache);
        
        // Keep only the most recent 50 products to limit size
        let limitedCache = {};
        if (cacheKeys.length > 50) {
          // Get the last 50 entries (simple approach - could be improved with LRU)
          const recentKeys = cacheKeys.slice(-50);
          recentKeys.forEach(key => {
            limitedCache[key] = sanitizeProductDetails(cache[key]);
          });
        } else {
          Object.keys(cache).forEach(key => {
            limitedCache[key] = sanitizeProductDetails(cache[key]);
          });
        }
        
        const serialized = JSON.stringify(limitedCache);
        if (serialized.length < 1 * 1024 * 1024) { // Limit to 1MB for product details
          localStorage.setItem("productDetailsCache", serialized);
        } else {
          console.warn("productDetailsCache too large, skipping persistence");
        }
      }
    } catch (err) {
      // Handle quota exceeded errors gracefully
      if (err.name === 'QuotaExceededError') {
        console.warn("localStorage quota exceeded, clearing old cache");
        try {
          localStorage.removeItem("multipleCategoryProducts");
          localStorage.removeItem("productDetailsCache");
        } catch (clearErr) {
          console.error("Failed to clear cache:", clearErr);
        }
      } else {
        console.warn("Failed to persist cache to localStorage:", err);
      }
    }
  };
  
  // Debounce writes - batch multiple category updates together
  // Clear any pending write and schedule a new one
  if (typeof window !== 'undefined') {
    if (writeTimeout) {
      clearTimeout(writeTimeout);
    }
    writeTimeout = setTimeout(() => {
      // Use requestIdleCallback for non-blocking writes, fallback to setTimeout
      if (window.requestIdleCallback) {
        window.requestIdleCallback(persistToStorage, { timeout: 2000 });
      } else {
        setTimeout(persistToStorage, 0);
      }
      writeTimeout = null;
    }, 500); // Batch writes within 500ms
  }
  
  return result;
};

export const store = configureStore({
  reducer: {

    products: productsSlice.reducer,
    multipleCategoryProducts: multipleCategoryProductsSlice.reducer,
    productDetails: productDetailsSlice.reducer,
    relatedProducts: relatedProductsSlice.reducer,
    cart: cartSlice.reducer,
    packages: packagesSlice.reducer,
    term: termSlice.reducer,
    orderImagesList: OrderCompletedImagesSlices.reducer,
    dateTime: dateTimeSlice.reducer,

    // categoryBar
    categoryBar: categoriesBarSlice.reducer,
    guestBlur: guestBlurSlice.reducer,
    guestLocationId: guestLocationIdSlice.reducer,
    activeCompany: activeCompanySlice.reducer
  },
  preloadedState: initialState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Increase threshold for immutable state check since we have large product cache
      // This reduces false warnings in development when state is large
      immutableCheck: {
        warnAfter: 128, // Increase from default 32ms to 128ms
      },
      // Increase threshold for serializable check as well
      serializableCheck: {
        warnAfter: 128, // Increase from default 32ms to 128ms
        // Ignore dateTime.arrivalDate and dateTime.arrivalTime since they're Date objects
        // This is acceptable - we'll convert them to strings when needed
        ignoredActions: [
          'DateTime/setArrivalDate',
          'DateTime/setArrivalTime',
        ],
        ignoredActionPaths: ['payload'],
        ignoredPaths: ['dateTime.arrivalDate', 'dateTime.arrivalTime'],
      },
    }).concat(persistProductCache),
});
